import i18n from 'i18next';
import {Icon, Show, Text} from 'platform/foundation';

import type {ComponentPropsWithRef} from 'react';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {BulkOperationPanelSummary} from './utils/getBulkOperationPanelSummary';

type BulkOperationsPanelChipProps = TestIdProps & {
  summary: BulkOperationPanelSummary;
  isOpen: boolean;
  buttonProps?: ComponentPropsWithRef<'button'>;
};

const STATUS_DOT_COLORS = {
  running: 'bg-severity-warning',
  done: 'bg-severity-success',
  error: 'bg-severity-danger',
  cancelled: 'bg-severity-warning',
};

function SummaryDot(props: {status: keyof typeof STATUS_DOT_COLORS}) {
  return (
    <span
      aria-hidden
      className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT_COLORS[props.status]}`}
    />
  );
}

/** Keep the footer compact; operation counts live in the complete panel summary. */
export function BulkOperationsPanelChip(props: BulkOperationsPanelChipProps) {
  return (
    <button
      {...props.buttonProps}
      type="button"
      aria-expanded={props.isOpen}
      className={`inline-flex cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-md border! border-solid! px-3! py-1.5! focus-visible:outline-2 focus-visible:outline-accent ${props.isOpen ? 'border-accent bg-palettes-blue-20-100! shadow-sm' : 'border-transparent bg-transparent!'}`}
      data-testid={suffixTestId('bulkOperationsPanelChip', props)}
    >
      <Text size="small">
        <strong>{i18n.t('general.actions.bulkActions')}</strong>
      </Text>
      <Show when={props.summary.runningCount > 0}>
        <SummaryDot status="running" />
      </Show>
      <Show when={props.summary.doneCount > 0}>
        <SummaryDot status="done" />
      </Show>
      <Show when={props.summary.errorCount > 0}>
        <SummaryDot status="error" />
      </Show>
      <Show when={props.summary.cancelledCount > 0}>
        <SummaryDot status="cancelled" />
      </Show>
      <span aria-hidden>
        <Icon value={props.isOpen ? 'navigation/expand_more' : 'navigation/expand_less'} size={4} />
      </span>
    </button>
  );
}
